import {connect} from "react-redux";
import {Game} from "@game/game";
import {Vector2} from "@utility/vector2";
import {
    CSSProperties,
    MouseEventHandler,
    PointerEventHandler,
    useCallback,
    useEffect,
    useRef,
    useState,
    WheelEventHandler
} from "react";
import {MapView} from "@components/map/MapView";
import {Bounds2} from "@utility/bounds2";
import {selectDragging, selectUser} from "@redux/selectors/map";
import {update as updateMap} from "@redux/slices/map"
import {Update} from "@redux/slices/base";
import {UserPlayer} from "@game/player/userPlayer";
import {DraggedTileController} from "@components/map/DraggedTileController";
import {Overlay} from "@components/interface/Overlay";
import {LETTER_THEME, TILE_SIZE} from "@components/core/theme";
import {GameEvent} from "@game/gameEvent";
import {Letter} from "@game/map/letter";

export interface IShovelAnim {
    letter: Letter;
    position: Vector2;
}

interface IFlyAnim {
    letter: Letter;
    startX: number;
    startY: number;
    endX: number;
    endY: number;
}

const FOCUS_OFFSET = new Vector2(0.5, 0.3);
const SNAP_RANGE = 20;
const SNAP_RANGE_SQUARED = SNAP_RANGE * SNAP_RANGE;

const MaximumYOffset = 25;
const MinimumYBorder = 50;
const MaximumZoom = 2;
const MinimumZoom = 0.5;

const styles: Record<string, CSSProperties> =  {
    container: {
        overflow: "hidden",
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        touchAction: "none"
    },
    contained: {
        position: "absolute"
    }
};

interface IContact {
    pointerId: number
    position: Vector2
}
interface IMapController {
    focus?: Vector2
    game: Game
    updateMap: Update
    userPlayer: UserPlayer
}

function MapController({focus, game, updateMap, userPlayer}: IMapController) {
    const [contacts, setContacts] = useState<IContact[]>([]);
    const [containerBounds, setContainerBounds] = useState(Bounds2.zeroEmpty);
    const [dragTarget, setDragTarget] = useState("");
    const [dragOffset, setDragOffset] = useState(Vector2.zero);
    const [dragPosition, setDragPosition] = useState(Vector2.zero);
    const [pointerPosition, setPointerPosition] = useState(Vector2.zero);
    const [insert, setInsert] = useState(-1);
    const [barBounds, setBarBounds] = useState(Bounds2.zeroEmpty);
    const [minimumYOffset, setMinimumYOffset] = useState(0);
    const [mapOffset, setMapOffset] = useState(Vector2.zero);
    const [screenTileOffset, setScreenTileOffset] = useState(Vector2.zero);
    const [shovelling, setShovelling] = useState(false);
    const [shovelAnim, setShovelAnim] = useState<IShovelAnim | null>(null);
    const [hiddenTilePos, setHiddenTilePos] = useState<Vector2 | null>(null);
    const [flyAnim, setFlyAnim] = useState<IFlyAnim | null>(null);
    const homePositionRef = useRef<Vector2>(userPlayer.getPosition(game.size));
    const viewportRegionRef = useRef<number | null>(null);
    // ── debug refs ────────────────────────────────────────────────────────────
    // contactsRef mirrors the contacts state so we can detect stale-closure mismatches
    const contactsRef = useRef<IContact[]>([]);
    const dragTargetRef = useRef("");
    const shovelAbortRef = useRef(false);
    const pendingHandUpdateRef = useRef<{hand: string[], shovels: number} | null>(null);
    const flyAnimDoneRef = useRef(false);
    const [viewportCenter, setViewportCenter] = useState(Vector2.zero);
    const [tileOffset, setTileOffset] = useState(Vector2.zero);
    const [touchOffset, setTouchOffset] = useState(Vector2.zero);
    const [zoomStartLength, setZoomStartLength] = useState(0);
    const [zoomStart, setZoomStart] = useState(1);
    const [zoom, setZoom] = useState(1);
    const refContainer = useRef<HTMLDivElement>(null);
    const containedRef = useRef<HTMLDivElement>(null);
    const dragPositionRef = useRef(Vector2.zero);
    const wheelDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const mapOffsetRef = useRef(mapOffset);
    const zoomRef = useRef(zoom);
    useEffect(() => {
        // During an active map pan, panMap owns mapOffsetRef and the DOM transform directly.
        // Writing the stale RAF-snapshot value back here resets mapOffsetRef to a position
        // several frames behind, causing the next panMap call to jump backward — the stutter.
        if (dragTargetRef.current === "mapView") return;
        mapOffsetRef.current = mapOffset;
        if (containedRef.current) {
            const x = mapOffset.x * zoomRef.current;
            const y = mapOffset.y * zoomRef.current;
            containedRef.current.style.transform = `translate(${x}px, ${y}px) scale(${zoomRef.current})`;
        }
    }, [mapOffset]);
    useEffect(() => {
        zoomRef.current = zoom;
        if (containedRef.current) {
            const x = mapOffsetRef.current.x * zoom;
            const y = mapOffsetRef.current.y * zoom;
            containedRef.current.style.transform = `translate(${x}px, ${y}px) scale(${zoom})`;
        }
    }, [zoom]);
    const dragTileOffset = dragPosition.minus(tileOffset).divide(TILE_SIZE).round();
    const dropPosition = dragTileOffset.multiply(TILE_SIZE).plus(tileOffset);
    const dropTile = screenTileOffset.plus(dragTileOffset);
    const playing = insert === -1 && !barBounds.contains(pointerPosition) && dropPosition.minus(dragPosition).lengthSquared() < SNAP_RANGE_SQUARED;
    const tilePosition = playing ? dropPosition : dragPosition;
    const draggingTile = dragTarget === "placedTileController" || dragTarget === "tileSlot";
    const draggingUser = dragTarget === "userPlayerView";

    const handleContextMenu: MouseEventHandler = (event) => {
        event.preventDefault();
    }

    const handleOverlayActive = useCallback(() => resetZoom(), []);

    const commitMapPan = () => {
        const finalOffset = mapOffsetRef.current;

        setMapOffset(finalOffset);
        updateMap({ update: finalOffset.serialize(), store: ["offset"] });
    };

    const handlePointerCancel: PointerEventHandler = (event) => {
        (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId);
        if (draggingTile) {
            if (! playing || ! game.userPlayer?.placeDraggedLetter(dropTile)) {
                game.userPlayer?.hand.dropLetter(insert);
            }
        }
        if (dragTarget === "mapView") commitMapPan();
        if (contacts.length === 2) setZoom(zoomRef.current);
        contacts.length > 0 && setContacts([]);
        contactsRef.current = [];
        dragTarget !== "" && setDragTarget("");
        dragTargetRef.current = "";
    }

    const handlePointerMove: PointerEventHandler = (event) => {
        const {pageX, pageY, pointerId} = event;
        const position = new Vector2(pageX, pageY);
        // Stale-closure check: contacts state may lag behind contactsRef by one render
        if (contacts.length !== contactsRef.current.length) {
            console.warn(`[pan] pointerMove STALE CLOSURE: state contacts=${contacts.length} ref contacts=${contactsRef.current.length} id=${pointerId} dragTarget=${dragTarget}`);
        }

        if (contacts.length === 2) {
            const updateContacts = contacts.map(contact => contact.pointerId === pointerId ?
                {pointerId, position} : contact);
            const zoomLength = contacts[0].position.minus(contacts[1].position).length();
            const newZoom = Math.max(MinimumZoom, Math.min(MaximumZoom, zoomStart * (zoomLength / zoomStartLength)));

            setContacts(updateContacts);
            zoomRef.current = newZoom;
            if (containedRef.current) {
                const x = mapOffsetRef.current.x * newZoom;
                const y = mapOffsetRef.current.y * newZoom;
                containedRef.current.style.transform = `translate(${x}px, ${y}px) scale(${newZoom})`;
            }
        } else {
            if (draggingTile || draggingUser) {
                setPointerPosition(position);
                setDragPosition(position.minus(dragOffset).minus(touchOffset));
            } else {
                if (dragTarget === "mapView") {
                    // getCoalescedEvents gives all intermediate touch samples merged into this
                    // event, so we step through each one rather than jumping to the final position
                    const coalesced = (event.nativeEvent as PointerEvent).getCoalescedEvents?.() ?? [];
                    const points = coalesced.length > 0
                        ? coalesced.map(e => new Vector2(e.pageX, e.pageY))
                        : [position];
                    for (const point of points) {
                        panMap(point);
                    }
                }
            }
        }
    }

    const handlePointerDown: PointerEventHandler = ({pageX, pageY, pointerId, target, currentTarget}) => {
        const position = new Vector2(pageX, pageY);

        if (contacts.length === 1) {
            setZoomStart(zoom);
            setZoomStartLength(contacts[0].position.minus(position).length());
            setDragTarget("");
        } else {
            const getDragTarget = (target: Element): string => target?.getAttribute ?
                target.getAttribute("data-draggable") || getDragTarget(target.parentNode as Element) : "";
            const targetBounds = new Bounds2((target as Element).getBoundingClientRect());
            const dragOffset = position.minus(targetBounds.min);
            let dragTarget = getDragTarget(target as Element);

            if ((dragTarget === "placedTileController" || dragTarget === "tileSlot") && ! game.userPlayer?.hand.dragging) {
                dragTarget = "mapView";
            }
            if (dragTarget === "mapView") {
                dragPositionRef.current = position;
                setDragPosition(position);
            } else {
                setPointerPosition(position);
                setDragPosition(position.minus(dragOffset).minus(touchOffset));
            }
            setDragTarget(dragTarget);
            dragTargetRef.current = dragTarget;
            setDragOffset(dragOffset);
            if (dragTarget !== "" && !(shovelling && dragTarget === "mapView")) {
                (currentTarget as HTMLElement).setPointerCapture(pointerId);
            }
        }
        setContacts(contacts => {
            const next = [...contacts, {pointerId, position}];
            contactsRef.current = next;
            return next;
        });
    };

    const handlePointerLeave = () => {
        if (draggingTile || draggingUser) return;
        if (dragTarget === "mapView") commitMapPan();
        contacts.length > 0 && setContacts([]);
        contactsRef.current = [];
        dragTarget !== "" && setDragTarget("");
        dragTargetRef.current = "";
        game.userPlayer?.hand.dragging && game.userPlayer?.hand.dropLetter(insert);
    };

    const handlePointerUp: PointerEventHandler = ({pointerId, currentTarget}) => {
        (currentTarget as HTMLElement).releasePointerCapture(pointerId);
        dragTargetRef.current = "";
        const prevContactCount = contacts.length;
        setContacts(contacts => {
            const next = contacts.filter(contact => contact.pointerId !== pointerId);
            contactsRef.current = next;
            return next;
        });
        if (prevContactCount === 2) {
            setZoom(zoomRef.current);
        }
        if (draggingTile) {
            if (! playing || ! game.userPlayer?.placeDraggedLetter(dropTile)) {
                game.userPlayer?.hand.dropLetter(insert);
            }
        }
        setDragTarget("");
        if (dragTarget === "mapView") {
            commitMapPan();
            const finalOffset = mapOffsetRef.current;
            const tileOff = TILE_SIZE.plus(finalOffset.mod(TILE_SIZE));
            const screenTileOff = finalOffset.divide(TILE_SIZE).floor().multiply(-1);
            const dragTileOff = dragPositionRef.current.minus(tileOff).divide(TILE_SIZE).round();
            userPlayer.move(screenTileOff.plus(dragTileOff)).catch(console.error);
        } else if (dragTarget !== "") {
            userPlayer.move(dropTile).catch(console.error);
        }
    };

    const handleShovel = useCallback(() => {
        setShovelling(shovelling => !shovelling)
    }, [setShovelling]);

    const handleHome = useCallback(() => {
        const pos = homePositionRef.current;
        const update = containerBounds.size().multiply(FOCUS_OFFSET)
            .minus(pos.multiply(TILE_SIZE))
            .maxY(minimumYOffset).minY(MaximumYOffset);

        mapOffsetRef.current = update;
        setMapOffset(update);
        updateMap({ update: update.serialize(), store: ["offset"] });
        if (containedRef.current) {
            containedRef.current.style.transform =
                `translate(${update.x * zoomRef.current}px, ${update.y * zoomRef.current}px) scale(${zoomRef.current})`;
        }
    }, [containerBounds, game, minimumYOffset, updateMap, userPlayer]);

    const handleWheel: WheelEventHandler = ({deltaY}) => {
        const newZoom = Math.max(MinimumZoom, Math.min(MaximumZoom, zoomRef.current + deltaY * -0.001));
        zoomRef.current = newZoom;
        if (containedRef.current) {
            const x = mapOffsetRef.current.x * newZoom;
            const y = mapOffsetRef.current.y * newZoom;
            containedRef.current.style.transform = `translate(${x}px, ${y}px) scale(${newZoom})`;
        }
        if (wheelDebounceRef.current) clearTimeout(wheelDebounceRef.current);
        wheelDebounceRef.current = setTimeout(() => {
            setZoom(zoomRef.current);
            wheelDebounceRef.current = null;
        }, 150);
    };

    const panMap = (move: Vector2) => {
        const delta = move.minus(dragPositionRef.current).divide(zoomRef.current);
        const deltaLen = Math.sqrt(delta.x * delta.x + delta.y * delta.y);
        if (deltaLen > 30) {
            console.warn(`[pan] LARGE DELTA ${deltaLen.toFixed(1)}px  move=(${move.x.toFixed(0)},${move.y.toFixed(0)}) dragPosRef=(${dragPositionRef.current.x.toFixed(0)},${dragPositionRef.current.y.toFixed(0)}) contacts=${contactsRef.current.length} zoom=${zoomRef.current.toFixed(2)}`);
        }
        const update = mapOffsetRef.current.plus(delta).min(Vector2.zero);
        mapOffsetRef.current = update;
        dragPositionRef.current = move;
        if (containedRef.current) {
            containedRef.current.style.transform =
                `translate(${update.x * zoomRef.current}px, ${update.y * zoomRef.current}px) scale(${zoomRef.current})`;
        }
    };

    const resetZoom = () => {
        if (zoomRef.current !== 1) {
            const interval = setInterval(() => {
                const current = zoomRef.current;
                const update = current > 0.99 && current < 1.01 ? 1 : (current + 1) / 2;
                zoomRef.current = update;
                if (containedRef.current) {
                    const x = mapOffsetRef.current.x * update;
                    const y = mapOffsetRef.current.y * update;
                    containedRef.current.style.transform = `translate(${x}px, ${y}px) scale(${update})`;
                }
                if (update === 1) {
                    clearInterval(interval);
                    setZoom(1);
                }
            }, 30);
        }
    }

    useEffect(() => {
        const applyHandUpdate = () => {
            const update = pendingHandUpdateRef.current;
            const hand = game.userPlayer?.hand;
            if (update && hand) {
                hand.updateLetters(update.hand);
                hand.updateShovels(update.shovels);
                pendingHandUpdateRef.current = null;
            }
        };
        const handleEvent = (event: GameEvent) => {
            if (event.type === "ShovelledTile") {
                const { hand, shovels } = event.properties as { hand: string[], shovels: number };
                setShovelling(false);
                setHiddenTilePos(null);
                if (flyAnimDoneRef.current) {
                    game.userPlayer?.hand.updateLetters(hand);
                    game.userPlayer?.hand.updateShovels(shovels);
                } else {
                    pendingHandUpdateRef.current = { hand, shovels };
                }
            } else if (event.type === "ShovelFailed") {
                shovelAbortRef.current = true;
                pendingHandUpdateRef.current = null;
                setHiddenTilePos(null);
                setShovelAnim(null);
                setFlyAnim(null);
            } else if (event.type === "ShovellingTile") {
                const { position, letter } = event.properties as { position: Vector2; letter: Letter };
                shovelAbortRef.current = false;
                flyAnimDoneRef.current = false;
                pendingHandUpdateRef.current = null;
                setHiddenTilePos(position);
                setShovelAnim({ letter, position });
                setTimeout(() => {
                    if (shovelAbortRef.current) return;
                    setShovelAnim(null);
                    const zoom = zoomRef.current;
                    const offset = mapOffsetRef.current;
                    const startX = (position.x * TILE_SIZE.x + offset.x) * zoom;
                    const startY = (position.y * TILE_SIZE.y + offset.y) * zoom;
                    const slots = document.querySelectorAll("[data-hand-slot]");
                    if (slots.length === 0) return;
                    const rect = slots[slots.length - 1].getBoundingClientRect();
                    setFlyAnim({ letter, startX, startY, endX: rect.right + 4, endY: rect.top });
                    setTimeout(() => {
                        if (shovelAbortRef.current) return;
                        flyAnimDoneRef.current = true;
                        setFlyAnim(null);
                        applyHandUpdate();
                    }, 540);
                }, 900);
            }
        }

        game.events.delegates.add(handleEvent);
        return () => game.events.delegates.remove(handleEvent);
    }, [game]);
    useEffect(() => {
        const handleEvent = (event: GameEvent) => {
            if (event.type === "Updated" || event.type === "Initialised" || event.type === "UserPlayerInitialised") {
                homePositionRef.current = userPlayer.getPosition(game.size);
            }
        };
        userPlayer.events.delegates.add(handleEvent);
        return () => userPlayer.events.delegates.remove(handleEvent);
    }, [userPlayer, game]);
    useEffect(() => {
        const updateBounds = () => {
            if (refContainer.current) {
                const clientRect = refContainer.current.getBoundingClientRect();

                setContainerBounds(new Bounds2(new Vector2(clientRect.x, clientRect.y),
                    new Vector2(clientRect.x + clientRect.width, clientRect.y + clientRect.height)));
            }
        }
        const handleResize = () => {
            updateBounds();
        };

        updateBounds();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [refContainer.current]);
    useEffect(() => {
        const minimumYOffset = containerBounds.max.y - game.size.mapTiles.multiply(TILE_SIZE).y -
            MinimumYBorder;

        setMinimumYOffset(minimumYOffset);
    }, [containerBounds, game]);
    useEffect(() => {
        if (focus) {
            const update = containerBounds.size().multiply(FOCUS_OFFSET)
                .minus(focus.multiply(TILE_SIZE))
                .maxY(minimumYOffset).minY(MaximumYOffset);

            mapOffsetRef.current = update;
            updateMap({
                update: update.serialize(),
                store: ["offset"]
            });
            setMapOffset(update);
        }
    }, [containerBounds, focus, game, minimumYOffset]);
    useEffect(() => {
        const mapOffsetPosition = new Vector2(mapOffset);

        setTileOffset(TILE_SIZE.plus(mapOffsetPosition.mod(TILE_SIZE)));
        setScreenTileOffset(mapOffsetPosition.divide(TILE_SIZE).floor().multiply(-1));
    }, [mapOffset]);
    useEffect(() => {
        if (containerBounds.size().x === 0) return;
        const center = containerBounds.size().multiply(0.5).minus(mapOffset).divide(TILE_SIZE);
        setViewportCenter(center);
        const regionId = game.size.getRegionId(game.size.getTileRegion(center));

        if (regionId !== viewportRegionRef.current) {
            viewportRegionRef.current = regionId;
            userPlayer.updateViewportRegions(center).catch(console.error);
        }
    }, [mapOffset, containerBounds, game, userPlayer]);
    useEffect(() => {
        if (dragTarget === "placedTileController" || dragTarget === "tileSlot" || dragTarget === "userPlayerView") {
            resetZoom();
        }
    }, [dragTarget]);
    useEffect(() => {
        setTouchOffset(("onTouchStart" in window) || navigator.maxTouchPoints > 0 ?
            TILE_SIZE.multiply(0.5) : Vector2.zero);
    }, []);
    const flyKeyframes = flyAnim ? `
@keyframes fly-to-slot {
    0%   { transform: translate(0px, 0px) scale(1); }
    33%  { transform: translate(0px, 0px) scale(1.2); }
    100% { transform: translate(${flyAnim.endX - flyAnim.startX}px, ${flyAnim.endY - flyAnim.startY}px) scale(1); }
}` : "";
    return (
        <div onContextMenu={handleContextMenu} onPointerCancel={handlePointerCancel} onPointerDown={handlePointerDown}
             onPointerLeave={handlePointerLeave} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp}
             onWheel={handleWheel} ref={refContainer} style={styles.container}>
            <div ref={containedRef} style={styles.contained}>
                {userPlayer &&
                    <MapView bounds={containerBounds} game={game} hiddenTilePos={hiddenTilePos ?? undefined}
                             mapOffset={mapOffset} shovelAnim={shovelAnim} shovelling={shovelling}
                             userPlayer={userPlayer} zoom={zoom}/>
                }
            </div>
            <Overlay dragPosition={draggingTile ? pointerPosition : Vector2.zero} game={game}
                     onActive={handleOverlayActive} onBarBounds={setBarBounds} onHome={handleHome} onShovel={handleShovel}
                     setInsert={setInsert} shovelling={shovelling} userPlayer={userPlayer}
                     viewportCenter={viewportCenter}
                     viewportTileBounds={new Bounds2(screenTileOffset,
                         screenTileOffset.plus(containerBounds.size().divide(TILE_SIZE.multiply(zoom))))}/>
            <DraggedTileController dragging={draggingTile} position={tilePosition} game={game}/>
            {flyAnim && (
                <>
                    <style>{flyKeyframes}</style>
                    <div style={{
                        position: "fixed",
                        left: flyAnim.startX,
                        top: flyAnim.startY,
                        width: TILE_SIZE.x,
                        height: TILE_SIZE.y,
                        ...LETTER_THEME.container,
                        backgroundColor: "white",
                        borderRadius: 8,
                        borderBottom: "slategray solid 3px",
                        borderRight: "slategray solid 3px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        animation: "fly-to-slot 540ms ease-in-out forwards",
                        pointerEvents: "none",
                        zIndex: 1000,
                    }}>
                        <span style={{...LETTER_THEME.content, color: "#000000", pointerEvents: "none"}}>
                            {flyAnim.letter.letter}
                        </span>
                    </div>
                </>
            )}
        </div>
    );
}
const mapStateToProps = (store: never) => ({
    dragging: selectDragging(store),
    user: selectUser(store)
});
const connected = connect(mapStateToProps, {
    updateMap
})(MapController);

export {connected as MapController}
